import React, { useCallback, useContext } from 'react';
import { Route, RouteProps } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Box, CircularProgress } from '@material-ui/core';
import { OrganizationContext } from './components/contexts/organization';
import { SmartCheckUnavailablePage } from './ecosystems/error/SmartCheckUnavailablePage';
import { Permission, UserContext } from './components/contexts/user/user-context';
import { NotAuthorized } from './ecosystems/error/NotAuthorized';

export const ProtectedRoute: React.FC<Required<Pick<RouteProps, 'component'>> & RouteProps> = ({
  component,
  location,
  ...rest
}) => {
  const onRedirecting = useCallback(
    () => (
      <Box display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    ),
    []
  );
  return (
    <Route
      component={withAuthenticationRequired(component, {
        onRedirecting,
      })}
      {...rest}
    />
  );
};

// ログインユーザーの権限と組織の権限によって、画面遷移できるかを判断する
export const AvailableRoute: React.FC<
  Required<Pick<RouteProps, 'component'>> & RouteProps & { checkSmartCheck?: boolean }
> = ({ component, checkSmartCheck, ...rest }) => {
  const organization = useContext(OrganizationContext);
  const { userInfo } = useContext(UserContext);

  /**
   * Checks if the organization has access to specific features based on SmartCheck
   * or BIG_SHOP_GROUP conditions.
   *
   * Access rules:
   * - 'show:coupon-pack-masters', 'show:coupon-packs': Requires SmartCheck feature enabled
   * - 'show:coupon-pack-usage-status': Requires either SmartCheck OR BIG_SHOP_GROUP org type
   *
   * @param permission - The permission string to check (e.g., 'show:coupon-pack-masters')
   * @returns NotAuthorized component if access denied, ProtectedRoute if access granted
   */
  const checkOrgIsSmartCheckOrBigShop = (permission: string) => {
    // Permissions that require SmartCheck feature
    const permissionCheckSmartCheck = ['show:coupon-pack-masters', 'show:coupon-packs'];
    // Permissions that require either SmartCheck OR BIG_SHOP_GROUP
    const permissionCheckSmartCheckOrBigShop = ['show:coupon-pack-usage-status'];

    // Deny access if:
    // 1. Permission requires SmartCheck but org doesn't have SmartCheck enabled, OR
    // 2. Org cannot create coupon masters and checkSmartCheck flag is true
    if (
      (permissionCheckSmartCheck.includes(permission) && organization.couponFeature?.apply.with !== 'smartCheck') ||
      (organization.availableCouponMaster === false && checkSmartCheck)
    ) {
      return <NotAuthorized />;
    }

    // Deny access if permission requires SmartCheck or BIG_SHOP_GROUP,
    // but org has neither SmartCheck nor BIG_SHOP_GROUP type
    if (
      permissionCheckSmartCheckOrBigShop.includes(permission) &&
      organization.couponFeature?.apply.with !== 'smartCheck' &&
      organization.organization?.type !== 'BIG_SHOP_GROUP'
    ) {
      return <NotAuthorized />;
    }

    return <ProtectedRoute component={component} {...rest} />;
  };

  // SmartCheckの場合、小店舗グループのみ画面遷移できるようにするため
  if (
    organization.availableCouponMaster === false &&
    !userInfo?.roles.includes('ShopManager') &&
    organization.organization?.type !== 'SHOP'
  ) {
    return <SmartCheckUnavailablePage />;
  }

  // ユーザー権限が読み込まれていない場合、ローディングする
  if (userInfo?.permissions.length === 0 || organization.loading) {
    return <CircularProgress />;
  }

  // クーポン詳細への直接アクセスはコンポーネント側で権限制御する
  if (location?.pathname.split('/')[3] === 'coupons') {
    return <ProtectedRoute component={component} {...rest} />;
  }

  // トップレベルでのURL直アクセスを防止する（ex: https://example.com/coupon-masters など）
  return userInfo?.permissions.includes(`show:${location?.pathname.split('/')[1]}` as Permission) ? (
    checkOrgIsSmartCheckOrBigShop(`show:${location?.pathname.split('/')[1]}`)
  ) : (
    <NotAuthorized />
  );
};
